// @flow
/* eslint-disable jsx-a11y/no-autofocus,no-param-reassign,consistent-return */

import React from 'react';
import keycode from 'keycode';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import MaskedInput, { conformToMask } from 'react-text-mask';
import AtomicInput from './AtomicInput';
import type { AtomicInputProps } from './AtomicInput';

const reSkippableSeparators = /[.,]/;
const reNegativeDomValue = /(^\$-\d|^\(.*\)$)/;
const reHasDigits = /\d/;
const reTrailingDecimal = /(\.(\d(\d)?)?)?$/;
const trailingDecimalFiller = '.00';

type CurrencyInputProps = AtomicInputProps<number | void> & {
  allowNegative: boolean,
  autoComplete: 'on' | 'off' | string,
  autoFocus?: boolean,
  autoSelect?: boolean,
  placeholder: string,
  commitOnKeyDown: boolean,
  commitOnBlur: boolean,
};

export default class CurrencyInput extends AtomicInput<number | void, string, CurrencyInputProps> {
  static defaultProps = {
    ...AtomicInput.defaultProps,
    required: false,
    allowNegative: false,
    autoFocus: false,
    autoSelect: false,
    placeholder: '',
    commitOnKeyDown: true,
    commitOnBlur: true,
  };

  textInput: ?HTMLInputElement = null;

  setTextInputRef = (element: HTMLInputElement) => {
    this.textInput = element;
  };

  focus() {
    if (this.textInput) {
      this.textInput.focus();
    }
  }

  domValueToValue(rawValue: string): number | void {
    if (rawValue === '') {
      return undefined;
    }
    const numeric = Number(rawValue.replace(/[^0-9.]/g, '').replace(/\.(?!\d+$)/g, ''));
    if (this.props.allowNegative && reNegativeDomValue.test(rawValue)) {
      return -numeric;
    }
    return numeric;
  }

  valueToDomValue(value: number | void): string {
    // Invalid numbers should just render as an empty string
    const strValue =
      Number.isNaN(value) || value === null || value === undefined ? '' : String(value);

    // If our domValue disagrees mask-input's conforming value, a brief dom-fight happens on
    // each render cycle which causes the caret position to be lost. We must ensure our currency
    // format matches the mask-input's *exactly*.

    // Begin by creating the mask function
    const maskFn = createNumberMask({
      allowDecimal: true,
      allowNegative: this.props.allowNegative,
    });
    // The number mask returns special "caret trap" entries which are only relevant during the
    // internal processing of mask-input. They must be removed before proceeding:
    const mask = maskFn(strValue).filter(maskChar => maskChar !== '[]');

    // This is masked input's own transform function
    const { conformedValue } = conformToMask(strValue, mask);

    // The actual mask-input we use also passes the confoming value through our "pipe" function,
    // so we must do the same:
    const processedValue = this.processMaskedValue(conformedValue);
    if (typeof processedValue === 'string') {
      return processedValue;
    }
    return processedValue.value;
  }

  processMaskedValue(conformingValue: string) {
    // Leave non-digit strings alone
    if (!reHasDigits.test(conformingValue)) {
      return conformingValue;
    }
    // Otherwise, pad the number string with trailing zeroes

    // $FlowFixMe - it will always match, exec will never return null
    const trailingDigitMatches = reTrailingDecimal.exec(conformingValue).slice(1, 4);
    let value = conformingValue;
    const indexesOfPipedChars = [];
    // For each match group that came up empty, add the corresponding trailing filler character
    for (const [idx, match] of trailingDigitMatches.entries()) {
      if (!match) {
        indexesOfPipedChars.push(value.length);
        value += trailingDecimalFiller[idx];
      }
    }
    return { value, indexesOfPipedChars };
  }

  handleKeyDown(ev: SyntheticKeyboardEvent<HTMLInputElement>) {
    const key = keycode(ev);

    if (key === 'enter' && this.props.commitOnKeyDown) {
      this.inputDidCommitChange();
    }

    if (!ev.currentTarget) {
      return;
    }
    // Backspace and delete should skip over separator chars.
    // The reason commas are handled in addition to decimal-dots is that the mask-input's own
    // behavior is inconsistent here - backspace skips, but forward delete does not.

    const { value, selectionStart, selectionEnd } = ev.currentTarget;
    // When we have selected more than just the separator, we want the delete to go through
    const selectionLength = selectionEnd - selectionStart;
    switch (key) {
      case 'delete':
        if (reSkippableSeparators.test(value[selectionStart])) {
          ev.currentTarget.selectionStart++;
          if (selectionLength <= 1) {
            ev.preventDefault();
          }
        }
        break;
      case 'backspace':
        if (reSkippableSeparators.test(value[selectionEnd - 1])) {
          ev.currentTarget.selectionEnd--;
          if (selectionLength <= 1) {
            ev.preventDefault();
          }
        }
        break;
      default:
    }
  }

  handleChange(ev: SyntheticInputEvent<HTMLInputElement>) {
    this.inputDidChange(ev.target.value);
  }

  handleBlur() {
    if (this.props.commitOnBlur) {
      this.inputDidCommitChange();
    }
  }

  render() {
    return (
      <MaskedInput
        render={(ref, props) => (
          <input
            ref={element => {
              ref(element);
              this.setTextInputRef(element);
            }}
            {...props}
          />
        )}
        mask={createNumberMask({
          allowDecimal: true,
          allowNegative: this.props.allowNegative,
        })}
        type="text"
        name={this.parent ? this.name : ''}
        value={this.domValue}
        placeholder={this.props.placeholder}
        style={{ textAlign: 'right' }}
        className={this.props.className}
        autoComplete={this.props.autoComplete}
        autoFocus={this.props.autoFocus}
        onKeyDown={ev => this.handleKeyDown(ev)}
        onChange={ev => this.handleChange(ev)}
        onFocus={ev => this.props.autoSelect && ev.target.select()}
        onBlur={() => this.handleBlur()}
        pipe={conformedValue => this.processMaskedValue(conformedValue)}
      />
    );
  }
}
